import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ENV } from "@app/env";
import { Store } from "@ngrx/store";
import { AngularFireDatabase } from "angularfire2/database";
import { AlertController, LoadingController, MenuController, ModalController, ToastController } from "ionic-angular";
import { isString } from "ionic-angular/util/util";
import { Observable } from "rxjs/Observable";
import { isObject } from "util";
import { IAdditionalService } from "../shared/models/reserve.model";
import * as sharedSelectors from "../shared/reducers/shared.reducer";

@Injectable()
export class Utilities {
  private clientId: string;

  constructor(
    public afDb: AngularFireDatabase,
    private http: HttpClient,
    private alertCtrl: AlertController,
    private menuCtrl: MenuController,
    private modalCtrl: ModalController,
    private loadingCtrl: LoadingController,
    private toastCtrl: ToastController,
    private store: Store<{}>,
  ) {
    this.store.select(sharedSelectors.getClientId).subscribe((clientId: string) => {
      if (clientId) {
        this.clientId = clientId;
      }
    });
  }

  public createToast(message: string, duration = 2000, position = "bottom") {
    const toast = this.toastCtrl.create({ message, duration, position });
    toast.present();
    return toast;
  }

  get FroalaKey() {
    return {
      imageUploadMethod: "POST",
      imageUploadParam: "image_param",
      imageUploadParams: { id: this.clientId },
      imageUploadURL: `${ENV.backend}/util/image/upload`,
      key: ENV.froalaKey,
    };
  }

  public setLoading(message: string = null) {
    const load = this.loadingCtrl.create({
      dismissOnPageChange: true,
    });

    if (message) {
      load.setContent(message);
    }

    load.present();

    return load;
  }

  public validateEventIdParam(eventId: string) {
    return (
      eventId !== "--" &&
      eventId !== "" &&
      eventId !== undefined &&
      eventId !== "undefined" &&
      eventId !== "%3AeventId" &&
      eventId !== ":eventId" &&
      eventId !== null &&
      eventId !== "null"
    );
  }

  public convertSecondsToTime(seconds) {
    const d = Number(seconds);
    const m = Math.floor(d % 3600 / 60);
    const s = Math.floor(d % 3600 % 60);

    return `${m < 10 ? "0" + m : m}:${s < 10 ? "0" + s : s}`;
  }

  public getDefaultCategories() {
    return this.getStaticInformationPromise("DefaultCategories")
      .once("value")
      .then((snap) => snap.val());
  }

  get defaultModulesProfileUsers(): string[] {
    return [
      "admin_stands",
      "admin_coupons",
      "admin_agenda",
      "admin_staff",
      "admin_resources",
    ];
  }

  public getDefaultModulesEventList(): string[] {
    return [
      "home",
      "notification",
      "my_reserves",
      "favorites",
      "diary",
      "staff",
      "news",
      "questions",
      "opinometer",
      "messages",
      "catalog",
      "sponsor",
      "sponsor_banner",
      "networking",
      "resource",
      "commercial_samples",
      "map",
      "timeline",
      "streaming",
      "social",
      "voting",
      "embed",
      "countdown",
      "checkin",
      "share_pages",
      // "interactivity",
      // "stand",
    ];
  }

  public getDefaultModulesEventDefault(): string[] {
    return [
      "notification",
      "my_reserves",
      "diary",
      "staff",
      "news",
      "catalog",
      "sponsor",
      "networking",
      "countdown",
      "resource",
      "raffle",
      "commercial_samples",
      "map",
      "streaming",
      "social",
      "urlRedirection",
    ];
  }

  public getTypesEvents(): string[] {
    return [
      "soccer",
      "golf",
      "party",
      "private",
      "public",
    ];
  }

  public getDefaultModulesClientList(): string[] {
    return [
      "notification",
      "pay_methods",
      "my_reserves",
      "favorites",
      "event",
      "staff",
      "likes",
      "news",
      "notes",
      "questions",
      "opinometer",
      "messages",
      "catalog",
      "sponsor",
      "sponsor_banner",
      "networking",
      "business",
      "gamification",
      "resource",
      "coupons",
      "referred",
      "raffle",
      "commercial_samples",
      "ticket",
      "guest",
      "services",
      "beneficiary",
      "pqr",
      // "embed",
      "directory",
      "sostenimiento",
      "terms",
      "legal_terms_service",
      "share_pages",
    ];
  }

  public generateRamdomKey() {
    return this.afDb.createPushId();
  }

  public copyToClipboard(idElement) {
    const element: any = document.getElementById(idElement);
    element.select();
    document.execCommand("copy");
    // element.setSelectionRange(0, 0);
    this.Toast.create({ message: "Copied to clipboard", position: "bottom", duration: 2000 }).present();
  }

  public defaultModuleConfig(module) {
    return new Promise((res, rej) => {
      this.http.get("./assets/json/modules_config.json").subscribe((config) => {
        if (config) {
          res(config[module]);
        } else {
          rej(`Module ${module} not found`);
        }
      });
    });
  }

  public getCurrencies() {
    return new Promise((res) => {
      this.http.get("./assets/json/currency.json").subscribe((currency) => res(currency));
    });
  }

  public parseDataInt(value) {
    return value ? parseInt(value, 10) : "";
  }

  public getStaticInformation(ref: string): Observable<any[]> {
    return this.afDb
      .list<any>(`StaticInformation/${ref}`)
      .valueChanges();
  }

  public getAdditionalServices(clientId: string): Observable<IAdditionalService[]> {
    return this.afDb
      .list<any>(`Clients/${clientId}/AdditionalServices`)
      .valueChanges();
  }

  public setAdditionalServices(clientId: string, services: IAdditionalService): Promise<any> {
    if (services.id === "") {
      services.id = this.afDb.createPushId();
    }

    return new Promise((resolve, reject) => {
      return this.afDb.database
        .ref(`Clients/${clientId}/AdditionalServices/${services.id}`)
        .update(services)
        .then(() => resolve(services))
        .catch((error) => reject(error));
    });
  }

  public getStaticInformationPromise(ref: string) {
    return this.afDb
      .list<any>(`StaticInformation/${ref}`).query.ref;
  }

  public getStaticInformationById(ref: string, refId: string): Observable<any> {
    return this.afDb
      .object<any>(`StaticInformation/${ref}/${refId}`)
      .valueChanges();
  }

  public isClearColor(colorHexa) {
    switch (colorHexa) {
      case "#fff":
      case "#f4f4f4":
        return "#000";
      default:
        return colorHexa;
    }
  }

  public getInvalidControlsForm(form: any) {
    const fields = [];
    Object.keys(form.controls).map((k) => {
      if (form.controls[k].status !== "VALID") {
        fields.push({ [k]: form.controls[k].status });
      }
    });
    return fields;
  }

  public getColors() {
    return this.getStaticInformationPromise("Colors")
      .once("value")
      .then((snap) => snap.val());
  }

  public getIcons() {
    return this.getStaticInformationPromise("IonIcons")
      .once("value")
      .then((snap) => snap.val());
  }

  get Alert() {
    return this.alertCtrl;
  }

  get Modal() {
    return this.modalCtrl;
  }

  get Toast() {
    return this.toastCtrl;
  }

  public setToast(message: string, duration: number = 2000, position: "top" | "middle" | "buttom" = "buttom") {
    return this.Toast.create({
      duration,
      message,
      position,
    }).present();
  }

  public generateID(initFlag = false, length = 6) {
    let text = "";
    const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

    if (isString(initFlag)) { text += initFlag; }
    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }

    return text;
  }

  public getMaterialIcons() {
    return this.getStaticInformationPromise("MaterialIcons")
      .once("value")
      .then((snap) => snap.val());
  }

  public getFontAwsomeIcons() {
    return this.getStaticInformationPromise("FontAwesomeIcons")
      .once("value")
      .then((snap) => snap.val());
  }

  public consoleLog(text = "", data: any) {
    if (ENV.mode === "Development") {
      // tslint:disable-next-line:no-console
      console.log(text, data);
    }
  }

  public consoleWarn(text = "", data: any) {
    if (ENV.mode === "Development") {
      console.warn(text, data);
    }
  }

  public consoleDecorator(text = "", data: any, color = "#CC732E") {
    if (ENV.mode === "Development") {
      // tslint:disable-next-line:no-console
      console.log(`%c ${text}`, `background: ${color}; color: white; display: block;`, data);
    }
  }

  public consoleError(text = "", data: any) {
    // tslint:disable-next-line:no-console
    console.log(`%c ${text}`, `background: red; color: white; display: block;`, data);
  }

  get isOpen() {
    return this.menuCtrl.isOpen();
  }

  get isClose() {
    return !this.menuCtrl.isOpen();
  }

  public underConstruction() {
    this.menuCtrl.close("main-menu");
    this.alertCtrl.create({
      buttons: [{
        role: "cancel",
        text: "Ok",
      }],
      // tslint:disable-next-line:max-line-length
      message: "At the moment this action is in maintenance. Please try again later or contact the Rebus Event Tech support!",
      title: "In maintenance",
    }).present();
  }

  public timeDifference(date2, date1 = null) {
    if (!date1) { date1 = new Date().getTime(); }
    let difference = date1 - date2;

    const daysDifference = Math.floor(difference / 1000 / 60 / 60 / 24);
    difference -= daysDifference * 1000 * 60 * 60 * 24;

    const hoursDifference = Math.floor(difference / 1000 / 60 / 60);
    difference -= hoursDifference * 1000 * 60 * 60;

    const minutesDifference = Math.floor(difference / 1000 / 60);
    difference -= minutesDifference * 1000 * 60;

    const secondsDifference = Math.floor(difference / 1000);

    return {
      day: daysDifference,
      hour: hoursDifference,
      min: minutesDifference,
      sec: secondsDifference,
    }
  }

  public searchItem(ev: string, item: any, arrStrg: any[]) {
    let state = false;
    if (ev) {
      arrStrg.forEach((s) => {
        if (isObject(s)) {
          const keys = Object.keys(s);
          keys.forEach((k) => {
            s[k].forEach((a) => {
              if (item[k]) {
                if (item[k][a]) {
                  try {
                    if (item[k][a].toLowerCase().indexOf(ev.toLowerCase()) > -1) {
                      state = true;
                    }
                  } catch (e) {
                    this.consoleError("Error", e);
                  }
                }
              }
            });
          });
        } else if (isString(s)) {
          if (item[s]) {
            try {
              if (item[s].toLowerCase().indexOf(ev.toLowerCase()) > -1) {
                state = true;
              }
            } catch (e) {
              this.consoleError("Error", e);
            }
          }
        } else {
          state = true;
        }
      });
    } else {
      state = true;
    }

    return state;
  }

  public getIconState(state) {
    switch (state) {
      case true:
        return "md-checkmark-circle";

      case false:
        return "md-close-circle";

      case "pending":
        return "time";

      case "EXPIRED_TRANSACTION":
        return "clock";

      case "PROCESS PAYMENT":
        return "md-calculator";

      case "RESTRICTED_CARD":
      case "PAYMENT_NETWORK_REJECTED":
        return "card";

      case "ABANDONED SHOPPING CART":
      case "DECLINED_TEST_MODE_NOT_ALLOWED":
      case "ABANDONED_TRANSACTION":
      case "INSUFFICIENT_FUNDS":
      case "INVALID_EXPIRATION_DATE_OR_SECURITY_CODE":
      case "failure":
        return "close-circle";

      case "success":
      case "CAPTURED":
      case "APPROVED":
        return "md-checkmark-circle";

      default:
        return "alert";
    }
  }

  public getColorState(state) {
    switch (state) {
      case true:
        return "secondary";

      case false:
        return "danger";

      case "pending":
      case "ABANDONED SHOPPING CART":
      case "ABANDONED_TRANSACTION":
        return "amber-800";

      case "failure":
      case "RESTRICTED_CARD":
      case "INSUFFICIENT_FUNDS":
      case "INVALID_EXPIRATION_DATE_OR_SECURITY_CODE":
      case "ABANDONED_TRANSACTION":
      case "EXPIRED_TRANSACTION":
      case "PAYMENT_NETWORK_REJECTED":
        return "red-900";

      case "success":
      case "CAPTURED":
      case "APPROVED":
        return "lightgreen-900";

      case "PROCESS PAYMENT":
        return "lightblue-500";

      default:
        return "primary";
    }
  }

  public getFilterTransactions(state) {
    switch (state) {
      case "success":
      case "CAPTURED":
      case "APPROVED":
        return true;

      default:
        return false;
    }
  }
}
// end file



// WEBPACK FOOTER //
// ./src/providers/utilities.ts